import { createContext, useContext, useReducer, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth.js';

// 인증 컨텍스트 생성
const AuthContext = createContext();

// 인증 상태 리듀서
const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, loading: true, error: null };
    case 'LOGIN_SUCCESS':
      return { 
        ...state, 
        user: action.payload.user, 
        token: action.payload.token, 
        isAuthenticated: true, 
        loading: false, 
        error: null 
      };
    case 'LOGIN_FAILURE':
      return { 
        ...state, 
        user: null, 
        token: null, 
        isAuthenticated: false, 
        loading: false, 
        error: action.payload 
      };
    case 'LOGOUT':
      return { 
        ...state, 
        user: null, 
        token: null, 
        isAuthenticated: false, 
        loading: false, 
        error: null 
      };
    case 'UPDATE_USER':
      return { ...state, user: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

// 인증 프로바이더 컴포넌트
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    token: null,
    isAuthenticated: false,
    loading: false,
    error: null,
  });

  const auth = useAuth();

  // useAuth 훅의 상태를 컨텍스트와 동기화
  useEffect(() => {
    if (auth.user && auth.token && auth.isAuthenticated) {
      // 로그인 상태일 때만 업데이트
      if (!state.isAuthenticated || state.user !== auth.user || state.token !== auth.token) {
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: auth.user, token: auth.token }
        });
      }
    } else if (!auth.isAuthenticated && state.isAuthenticated) {
      // 로그아웃 상태로 변경될 때만 업데이트
      dispatch({ type: 'LOGOUT' });
    }
  }, [auth.user, auth.token, auth.isAuthenticated]); // state.isAuthenticated 의존성 제거하여 무한 루프 방지

  // 로그인 함수
  const login = async (credentials) => {
    dispatch({ type: 'LOGIN_START' });
    const result = await auth.login(credentials);
    
    if (result.success) {
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: result.user, token: auth.token }
      });
    } else {
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: result.error
      });
    }
    
    return result;
  };

  // 로그아웃 함수
  const logout = async () => {
    await auth.logout();
    dispatch({ type: 'LOGOUT' });
  };

  // 회원가입 함수
  const register = async (credentials) => {
    dispatch({ type: 'LOGIN_START' });
    const result = await auth.register(credentials);
    
    if (!result.success) {
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: result.error
      });
    } else {
      dispatch({ type: 'CLEAR_ERROR' });
    }
    
    return result;
  };

  // 회원탈퇴 함수
  const unregister = async (credentials) => {
    dispatch({ type: 'LOGIN_START' });
    const result = await auth.unregister(credentials);
    
    if (result.success) {
      dispatch({ type: 'LOGOUT' });
    } else {
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: result.error
      });
    }
    
    return result;
  };

  // 사용자 정보 업데이트
  const updateUser = (userData) => {
    dispatch({ type: 'UPDATE_USER', payload: userData });
  };

  // 에러 초기화
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value = {
    ...state,
    login,
    logout,
    register,
    unregister,
    updateUser,
    clearError,
    refreshToken: auth.refreshToken,
    fetchUser: auth.fetchUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// 인증 컨텍스트 사용 훅
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext는 AuthProvider 내부에서만 사용할 수 있습니다.');
  }
  return context;
};

export default AuthContext;
